import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { faqCatCrud } from "../api/ApiRoutesFile";

export const faqCategoryApiSlice = createApi({
  reducerPath: "faqCategoryApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "https://castle-auction-production.up.railway.app/api/",
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      const token = localStorage.getItem("auction_portal");
      if (token) {
        headers.set("x-auth-token", token);
      }
      return headers;
    },
  }),
  tagTypes: ["FaqCategories"],
  endpoints: (builder) => ({
    faqCategories: builder.query({
      query: ({ endpoint, id }) => {
        const url = id ? `${endpoint}/${id}` : endpoint;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.categories.map(({ _id }) => ({
                type: "FaqCategories",
                id: _id,
              })),
              { type: "FaqCategories", id: "LIST" },
            ]
          : [{ type: "FaqCategories", id: "LIST" }],
    }),

    createFaqCategory: builder.mutation({
      query: ({ endpoint, formData }) => ({
        url: endpoint,
        method: "POST",
        body: formData,
      }),
      // Wait for the API response before updating the cache
      async onQueryStarted(
        { endpoint, formData, lastId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled; // wait for the real API response
          if (data && data.category && data.category._id) {
            // Update the paginated query cache (if using a page-based cache)
            dispatch(
              faqCategoryApiSlice.util.updateQueryData(
                "faqCategories",
                { endpoint: faqCatCrud, id: lastId },
                (draft) => {
                  draft.categories.unshift(data.category);
                }
              )
            );
          }
        } catch (error) {
          console.error("Create failed:", error);
        }
      },
    }),

    updateFaqCategory: builder.mutation({
      query: ({ endpoint, id, formData }) => ({
        url: `${endpoint}/${id}`,
        method: "PUT",
        body: formData,
      }),
      // Wait for API response before updating the cache
      async onQueryStarted(
        { endpoint, id, formData, lastId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled; // wait for the API call to complete
          if (data && data.category) {
            // Update paginated cache if applicable
            dispatch(
              faqCategoryApiSlice.util.updateQueryData(
                "faqCategories",
                { endpoint: faqCatCrud, id: lastId },
                (draft) => {
                  const index = draft.categories.findIndex((a) => a._id === id);
                  if (index !== -1) {
                    draft.categories[index] = data.category;
                  }
                }
              )
            );
          }
        } catch (error) {
          console.error("Update failed:", error);
          // Handle rollback if needed
        }
      },
    }),

    deleteFaqCategory: builder.mutation({
      query: ({ endpoint, id }) => ({
        url: `${endpoint}/${id}`,
        method: "DELETE",
      }),

      async onQueryStarted(
        { endpoint, id, lastId },
        { dispatch, queryFulfilled }
      ) {
        try {
          // Wait for API response before updating the cache
          const { data } = await queryFulfilled;

          if (data && data?.category) {
            dispatch(
              faqCategoryApiSlice.util.updateQueryData(
                "faqCategories",
                { endpoint: faqCatCrud, id: lastId },
                (draft) => {
                  if (draft && draft.categories) {
                    draft.categories = draft.categories.filter(
                      (faq) => faq._id !== id
                    );
                  }
                }
              )
            );
          }
        } catch (error) {
          console.error("Delete FAQ failed:", error);
        }
      },
    }),
  }),
});

export const {
  useFaqCategoriesQuery,
  useCreateFaqCategoryMutation,
  useUpdateFaqCategoryMutation,
  useDeleteFaqCategoryMutation,
} = faqCategoryApiSlice;
